import api from '@/lib/api/axios'

export interface Classification {
    id: number
    Name: string
    /** Immutable identity for base-taxonomy rows; scripts and behavioural
     *  rules key on this, which is what makes Name freely renameable. */
    SystemKey?: string | null
    Category: string
    DisplayRank: number
    Description?: string | null
    BackgroundColor?: string | null
    ObjectCount?: number
    Active?: number
    IsSystem?: number
    /**
     * Whether recovering something of this kind needs an engineer physically in front of it:
     * 'on-site' | 'remote', or null for undecided. Drives who is even eligible to be assigned the
     * object on the Engineer Assignments screen.
     */
    AccessMode?: string | null
}

export interface ClassificationInput {
    Name: string
    Category: string
    DisplayRank: number
    Description?: string | null
    BackgroundColor?: string | null
    AccessMode?: string | null
    /** Shipped with the product. Accepted only where shipped content is authored. */
    IsSystem?: boolean
}

/**
 * One graphic assigned to a classification.
 *
 * ManufacturerID null is the DEFAULT — what a node draws when its vendor is unknown or has no
 * variant of its own. The database enforces one default per classification; the vendor comes from
 * the object's Technology, never from the shape's tags.
 */
export interface ClassificationGraphic {
    id?: number
    ClassificationID: number
    ShapeId: number
    /** Exactly one per classification. The graphic drawn whenever no candidate matches. */
    IsDefault: boolean
    ShapeFilename?: string
    ShapeCategory?: string
    /**
     * The shape's own tags, from the library. Carried through because they are the entire reason
     * a candidate would ever be chosen — matched against each object's description, manufacturer,
     * model and technology category at render time. A candidate whose tags say nothing the
     * objects say never draws, and the assignment screen is the only place to notice.
     */
    ShapeTags?: string | null
    /** The artwork's pixel size, captured at upload. Null for a shape uploaded before it was. */
    ShapeWidth?: number | null
    ShapeHeight?: number | null
}

export interface ClassificationRule {
    id: number
    DependentClassID: number
    ProviderClassID: number
    DependentName: string
    ProviderName: string
}

/** How much relies on one rule: dependencies on the canvas (every scenario) and in templates. */
export interface ClassificationRuleUsage {
    DependentClassID: number
    ProviderClassID: number
    MappingCount: number
    TemplateDependencyCount: number
}

export const classificationsApi = {
    list: () =>
        api.get<{ data: Classification[] }>('/api/classifications'),

    rules: () =>
        api.get<{ data: ClassificationRule[] }>('/api/classifications/rules'),

    // Classifications this object may hold without invalidating any existing
    // mapping — backs the canvas drag (legal bands) and the edit dropdown.
    legalForObject: (objectId: string) =>
        api.get<{ data: Classification[] }>(
            `/api/classifications/legal-for-object/${encodeURIComponent(objectId)}`
        ),

    validateChange: (objectId: string, classificationId: number) =>
        api.post<{ data: { allowed: boolean; violations: unknown[] } }>(
            '/api/classifications/validate-change',
            { ObjectID: objectId, ClassificationID: classificationId }
        ),

    // ── Admin (classifications.* permissions required server-side) ──
    adminList: () =>
        api.get<{ data: Classification[] }>('/api/classifications?includeInactive=1'),

    create: (data: ClassificationInput) =>
        api.post<{ data: Classification }>('/api/classifications', data),

    update: (id: number, data: Partial<ClassificationInput>) =>
        api.put<{ data: Classification }>(`/api/classifications/${id}`, data),

    setActive: (id: number, active: boolean) =>
        api.patch<{ data: { id: number; Active: number } }>(
            `/api/classifications/${id}/active`,
            { Active: active }
        ),

    remove: (id: number) =>
        api.delete(`/api/classifications/${id}`),

    // Whether this deployment is where shipped content is authored (SHIPPED_CONTENT_AUTHORING_ENABLED).
    authoring: () =>
        api.get<{ data: { enabled: boolean } }>('/api/classifications/authoring'),

    addRule: (dependentClassId: number, providerClassId: number) =>
        api.post('/api/classifications/rules', {
            DependentClassID: dependentClassId,
            ProviderClassID: providerClassId,
        }),

    deleteRule: (ruleId: number) =>
        api.delete(`/api/classifications/rules/${ruleId}`),

    // The rules editor: what relies on each rule, and one classification's rules saved whole.
    ruleUsage: () =>
        api.get<{ data: ClassificationRuleUsage[] }>('/api/classifications/rules/usage'),

    setRules: (id: number, rules: { DependsOn: number[]; DependedOnBy: number[] }) =>
        api.put<{ data: { added: number; removed: number } }>(`/api/classifications/${id}/rules`, rules),

    // ── Graphics ──
    // Every assignment in one call. The list screen needs a "has a graphic" mark per row, and one
    // request beats forty-nine.
    graphics: () =>
        api.get<{ data: ClassificationGraphic[] }>('/api/classifications/graphics'),

    /**
     * Replace the whole set for one classification.
     *
     * Replace rather than patch because the set has an invariant the rows cannot express
     * individually — at most one default, at most one per vendor — and a sequence of individual
     * edits passes through states that break it.
     */
    setGraphics: (id: number, graphics: { ShapeId: number; IsDefault: boolean }[]) =>
        api.put<{ data: { ClassificationID: number; count: number } }>(
            `/api/classifications/${id}/graphics`,
            { graphics }
        ),
}
